import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ExternalLink, Download, AlertCircle, FileText } from 'lucide-react';


interface WordOnlineViewerProps {
  documentUrl: string;
  fileName?: string;
  className?: string;
  height?: string;
  onFileUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const loadingMessages = [
  "Preparing your document...",
  "Opening in Microsoft Office...",
  "Almost ready..."
];

export const WordOnlineViewer: React.FC<WordOnlineViewerProps> = ({
  documentUrl,
  fileName = 'Document',
  className = '',
  height = '600px',
  onFileUpload
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showOverlay, setShowOverlay] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const loadStartTime = useRef<number>(Date.now());
  const minimumDisplayTime = 1500; // 1.5 seconds minimum overlay display

  // Reset states when document URL changes
  useEffect(() => {
    setIsLoading(true);
    setShowOverlay(true);
    setHasError(false);
    setFadeOut(false);
    setLoadingMessageIndex(0);
    loadStartTime.current = Date.now();
    setRefreshKey(prev => prev + 1);
  }, [documentUrl]);

  // Cycle through loading messages
  useEffect(() => {
    if (!isLoading) return;
    
    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [isLoading]);

  // Build embed URL without excessive cache-busting (only use refreshKey)
  const embedUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(documentUrl)}&wdStartOn=1`;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openInNewTab = () => {
    window.open(embedUrl, '_blank');
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    
    // Calculate remaining time to show overlay (minimum display time)
    const elapsed = Date.now() - loadStartTime.current;
    const remainingTime = Math.max(0, minimumDisplayTime - elapsed);
    
    // Start fade out after minimum display time
    setTimeout(() => {
      setFadeOut(true);
      // Remove overlay after fade animation completes
      setTimeout(() => {
        setShowOverlay(false);
      }, 300); // Match CSS transition duration
    }, remainingTime);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setShowOverlay(false);
    setHasError(true);
    console.error('Failed to load document:', documentUrl);
  };

  return (
    <div className={`${className} h-full`}>
      <div className="h-full flex flex-col">
        {/* Document viewer container */}
        <div className="relative w-full h-full bg-muted/30 rounded-lg overflow-hidden">
          
          {/* Branded Loading Overlay */}
          {showOverlay && (
            <div 
              className={`absolute inset-0 z-20 flex flex-col items-center justify-center bg-background transition-opacity duration-300 ${
                fadeOut ? 'opacity-0' : 'opacity-100'
              }`}
            >
              <div className="text-center space-y-6">
                {/* Loading message with smooth transition */}
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-5 h-5 text-primary animate-bounce" />
                    <span className="text-lg font-medium text-foreground">
                      {loadingMessages[loadingMessageIndex]}
                    </span>
                  </div>
                  
                  {/* Progress dots */}
                  <div className="flex justify-center gap-1.5">
                    {[0, 1, 2].map((i) => (
                      <div 
                        key={i}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          i <= loadingMessageIndex 
                            ? 'bg-primary scale-100' 
                            : 'bg-muted-foreground/30 scale-75'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Subtle file name display */}
                <p className="text-sm text-muted-foreground max-w-xs truncate px-4">
                  {fileName}
                </p>
              </div>
            </div>
          )}

          {/* Error state */}
          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
              <div className="text-center p-6 max-w-md">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
                <h3 className="font-semibold mb-2">Document Failed to Load</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  The document could not be displayed. This might be because:
                </p>
                <ul className="text-xs text-muted-foreground text-left mb-4 space-y-1">
                  <li>• The URL is not publicly accessible</li>
                  <li>• The file is not a valid .docx document</li>
                  <li>• Microsoft's viewer service is unavailable</li>
                </ul>
                <div className="flex gap-2 justify-center">
                  <Button size="sm" variant="outline" onClick={openInNewTab}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Try in New Tab
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDownload}>
                    <Download className="w-4 h-4 mr-2" />
                    Download File
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Iframe - always rendered but behind overlay */}
          <iframe 
            key={`doc-${refreshKey}`}
            src={embedUrl}
            width="100%"
            height="100%"
            frameBorder="0"
            title={`Document Viewer - ${fileName}`}
            className="w-full h-full border-0"
            style={{ 
              outline: 'none', 
              border: 'none',
              visibility: showOverlay ? 'hidden' : 'visible'
            }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        </div>
      </div>
    </div>
  );
};
